import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from '@c/icons';
import BaseModal from '@c/modals/BaseModal';
import { formatCurrency } from '@util/index';
import { XIcon } from 'lucide-react';
import { ProductDocument } from 'models/product';
import { ReviewDocument } from 'models/review';
import Image from 'next/image';
import { SellerAccolade } from '@c/seller/Accolades';

interface ReviewModalProps {
  review: ReviewDocument;
  open: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  product?: ProductDocument;
}

export default function ReviewModal({
  review,
  product,
  open,
  onClose,
  onPrev,
  onNext,
}: ReviewModalProps) {
  return (
    <BaseModal isOpen={open} dismiss={onClose} hideCloseIcon>
      <div className="flex max-w-full gap-x-4">
        <div className="relative flex-shrink-0">
          {onPrev && (
            <button
              type="button"
              aria-label="Previous review"
              onClick={onPrev}
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2 hover:bg-white"
            >
              <ChevronLeftIcon className="h-8 w-8 text-brand-secondary" />
            </button>
          )}
          <Image
            src={review.images?.[0]?.thumb ?? ''}
            alt={review.comment}
            width={100}
            height={100}
            className="rounded-y-[3rem] h-[80rem] w-[60rem] rounded-l-[3rem] bg-zinc-100 object-contain"
          />
          {onNext && (
            <button
              type="button"
              aria-label="Next review"
              onClick={onNext}
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2 hover:bg-white"
            >
              <ChevronRightIcon className="h-8 w-8 text-brand-secondary" />
            </button>
          )}
        </div>

        <div className="flex h-full w-[60rem] max-w-[60vw] flex-col gap-16 overflow-auto bg-white p-8 pr-16">
          <div className="flex h-full flex-col justify-between">
            <div className="flex h-[60rem] flex-col gap-y-16">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-x-4">
                  <Image
                    src={review.reviewer_photo}
                    alt={review.reviewer_username}
                    width={100}
                    height={100}
                    className="h-[5rem] w-[5rem] rounded-full"
                  />

                  <div className="flex flex-col gap-y-1">
                    <span className="text-[1.8rem] font-semibold text-black">
                      {review.reviewer_username}
                    </span>

                    <span className="text-[1.4rem] font-medium text-brand-gray">
                      {new Date(review.created).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                </div>

                <button type="button" aria-label="Close" onClick={onClose}>
                  <XIcon className="h-[2.5rem] w-[2.5rem] text-brand-gray" />
                </button>
              </div>

              <div className="flex items-center gap-x-2">
                {[...Array(5)].map((_, i) => (
                  <StarIcon
                    key={i}
                    className={`h-[4rem] w-[4rem] ${
                      i < review.rating
                        ? 'fill-brand-secondary text-transparent'
                        : 'fill-zinc-300 text-transparent'
                    }`}
                  />
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-x-8 gap-y-6">
                {review.fast_shipper && (
                  <SellerAccolade accolade="fast_shipper" count={1} />
                )}
                {review.as_described && (
                  <SellerAccolade accolade="accurate_describer" count={1} />
                )}
                {review.quick_replies && (
                  <SellerAccolade accolade="quick_responder" count={1} />
                )}
              </div>

              <p className="overflow-auto whitespace-pre-line break-words text-[2.1rem] font-medium text-brand-dark-gray">
                {review.comment}
              </p>
            </div>

            {product && (
              <div className="flex w-full flex-col gap-y-4">
                <span className="text-[1.5rem] font-medium text-brand-dark-gray">
                  Purchased item:
                </span>

                <div className="flex items-center gap-x-4">
                  <Image
                    src={product.images?.[0]?.thumb ?? ''}
                    alt={product.title ?? ''}
                    width={100}
                    height={100}
                    className="h-[10rem] w-[10rem] rounded-[1rem]"
                  />

                  <div className="flex flex-col gap-y-2">
                    <h3 className="line-clamp-2 text-[2.1rem] font-semibold text-brand-secondary">
                      {product.title}
                    </h3>

                    <span className="text-[2.7rem] font-semibold text-black">
                      {formatCurrency(product.price ?? 0)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
